import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockOpen } from '@fortawesome/free-solid-svg-icons';
import FormResultMessage from './FormResultMessage';
import {
  fetchHVACampaigns,
  HVACampaign,
  fetchHVAUserAssignedCampaigns,
  assignUserCampaigns
} from 'services/hvaservicecampaign.service';
import { fetchAccounts, Account } from 'services/account.service';

const CampaignAssignmentForm: React.FC = () => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(
    new Set()
  );

  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<
    'successupdate' | 'errorupdate'
  >('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignsData = await fetchHVACampaigns();
        setCampaigns(campaignsData);
      } catch (error) {
        console.error('Error loading campaigns:', error);
      }
    };

    const loadAccounts = async () => {
      try {
        const accountsData = await fetchAccounts();
        const filteredAccounts = accountsData.filter(
          account => !account.roles.includes('systemadmin')
        );
        setAccounts(filteredAccounts);
      } catch (error) {
        console.error('Error loading accounts:', error);
      }
    };

    loadCampaigns();
    loadAccounts();
  }, []);

  useEffect(() => {
    const loadUserCampaigns = async () => {
      if (selectedUser !== null) {
        try {
          // eslint-disable-next-line
          const campaignUserIds: number[] = await fetchHVAUserAssignedCampaigns(selectedUser);
          setSelectedCampaigns(new Set(campaignUserIds));
        } catch (error) {
          console.error('Error loading user campaigns:', error);
        }
      } else {
        setSelectedCampaigns(new Set());
      }
    };

    loadUserCampaigns();
  }, [selectedUser]);

  const handleUserSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUser(Number(event.target.value));
  };

  const handleCampaignSelect = (campaignId: number) => {
    setSelectedCampaigns(prevSelectedCampaigns => {
      const newSelectedCampaigns = new Set(prevSelectedCampaigns);
      if (newSelectedCampaigns.has(campaignId)) {
        newSelectedCampaigns.delete(campaignId);
      } else {
        newSelectedCampaigns.add(campaignId);
      }
      return newSelectedCampaigns;
    });
  };

  const handleAssign = async () => {
    if (selectedUser !== null && selectedCampaigns.size > 0) {
      try {
        await assignUserCampaigns(selectedUser, Array.from(selectedCampaigns));
        setMessage(
          'Welldone! The selected campaigns have now been assigned to the user.'
        );
        setMessageType('successupdate');
      } catch (error) {
        setMessage('I apologize, but it seems there was a hiccup! ' + error);
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    } else {
      alert('Please select both a user and at least one campaign.');
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <Row className="p-0">
      <Col className="">
        <h3 className="px-2 text-secondary-darker">Users</h3>
        <Card className="bg-body">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '250px', overflowY: 'scroll' }}
              >
                {accounts.map(account => (
                  <Form.Check
                    key={account.id}
                    type="radio"
                    name="users"
                    id={`user-${account.id}`}
                    label={`${account.fullname} - (${account.email})`}
                    value={account.id}
                    onChange={handleUserSelect}
                    checked={selectedUser === account.id}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
      <Col className="campaigns-column">
        <h3 className="px-2 text-secondary-darker">Campaigns</h3>
        <Card className="bg-body campaignassigment-card">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '250px', overflowY: 'scroll' }}
              >
                {campaigns.map(campaign => (
                  <Form.Check
                    key={campaign.id}
                    type="checkbox"
                    label={`(${campaign.servicename}) - ${campaign.name}`}
                    onChange={() => handleCampaignSelect(campaign.id)}
                    checked={selectedCampaigns.has(campaign.id)}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
        <div className="primary pt-3">
          <Button
            className="btn btn-primary btn-block px-4"
            onClick={handleAssign}
          >
            <FontAwesomeIcon icon={faLockOpen} className="me-2 fs-9" />
            Grant access to the selected campaigns
          </Button>
        </div>
      </Col>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </Row>
  );
};

export default CampaignAssignmentForm;
